import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SecurityRegistryHistory = () => {
  const [registries, setRegistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { user, logout } = useAuth();

  useEffect(() => {
    fetchRegistries();
  }, []);

  const fetchRegistries = async () => {
    try {
      const response = await axios.get(`${API}/security/registry?limit=500`);
      setRegistries(response.data);
    } catch (error) {
      console.error('Error fetching registries:', error);
      toast.error('Error al cargar registros de seguridad');
    } finally {
      setLoading(false);
    }
  };

  const filteredRegistries = registries.filter(registry => 
    registry.room_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    registry.registered_by.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (registry.vehicle_plate && registry.vehicle_plate.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePrintPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4'); // landscape, millimeters, A4
    
    // Add title
    doc.setFontSize(18);
    doc.text('Historial de Registros de Seguridad', 14, 20);
    
    // Add date
    doc.setFontSize(11);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`, 14, 28);
    
    // Prepare table data
    const headers = [['Habitación', 'Personas', 'Destinos', 'Placa', 'Marca', 'Color', 'Comentarios', 'Registrado Por', 'Fecha']];
    const data = filteredRegistries.map(registry => [
      registry.room_number,
      registry.number_of_people.toString(),
      registry.destination.join(', '),
      registry.vehicle_plate || '-',
      registry.vehicle_brand || '-',
      registry.vehicle_color || '-',
      registry.comments || '-',
      registry.registered_by,
      formatDate(registry.registered_at)
    ]);
    
    // Add table
    autoTable(doc, {
      head: headers,
      body: data,
      startY: 35,
      theme: 'grid',
      headStyles: {
        fillColor: [102, 126, 234],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      bodyStyles: {
        fontSize: 8
      },
      columnStyles: {
        0: { cellWidth: 20 },  // Habitación
        1: { cellWidth: 18 },  // Personas
        2: { cellWidth: 40 },  // Destinos
        3: { cellWidth: 22 },  // Placa
        4: { cellWidth: 25 },  // Marca
        5: { cellWidth: 20 },  // Color
        6: { cellWidth: 35 },  // Comentarios
        7: { cellWidth: 30 },  // Registrado Por
        8: { cellWidth: 35 }   // Fecha
      },
      margin: { top: 35, left: 14, right: 14 }
    });
    
    // Save PDF
    doc.save(`registros_seguridad_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF descargado exitosamente');
  };

  const handleExportCSV = () => {
    const headers = ['Habitación', 'No. Personas', 'Destinos', 'Placa', 'Marca', 'Color', 'Comentarios', 'Registrado Por', 'Fecha'];
    
    const csvData = filteredRegistries.map(registry => [
      registry.room_number,
      registry.number_of_people,
      registry.destination.join('; '),
      registry.vehicle_plate || '',
      registry.vehicle_brand || '',
      registry.vehicle_color || '',
      registry.comments || '',
      registry.registered_by,
      formatDate(registry.registered_at)
    ]);

    const csv = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `registros_seguridad_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('Registros exportados a CSV');
  };

  return (
    <div className="security-registry-history-container" data-testid="security-registry-history">
      <div className="admin-header">
        <div className="header-left">
          <h1 data-testid="registry-history-title">Historial de Registros de Seguridad</h1>
          <p className="user-info">Administrador: <strong>{user?.username}</strong></p>
        </div>
        <div className="header-actions">
          <Link to="/history" className="back-button" data-testid="back-history-btn">
            Volver al Historial
          </Link>
          <button onClick={logout} className="logout-button" data-testid="logout-btn">
            Cerrar Sesión
          </button>
        </div>
      </div>

      <div className="filters-section">
        <div className="form-group">
          <label>Buscar</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por habitación, usuario o placa"
            data-testid="search-input"
          />
        </div>
        <div style={{display: 'flex', gap: '1rem', alignItems: 'flex-end'}}>
          <button 
            className="btn-export"
            onClick={handleExportCSV}
            data-testid="export-csv-btn"
          >
            📊 Descargar CSV
          </button>
          <button 
            className="btn-export"
            onClick={handlePrintPDF}
            data-testid="print-pdf-btn"
          >
            📄 Imprimir PDF
          </button>
        </div>
      </div>

      <div className="history-content">
        {loading ? (
          <div className="loading">Cargando registros...</div>
        ) : filteredRegistries.length === 0 ? (
          <div className="no-visitors">
            <div className="empty-state-icon">📋</div>
            <p>No se encontraron registros</p>
          </div>
        ) : (
          <div className="history-table">
            <table data-testid="registry-table">
              <thead>
                <tr>
                  <th>Habitación</th>
                  <th>No. Personas</th>
                  <th>Destinos</th>
                  <th>Placa</th>
                  <th>Marca</th>
                  <th>Color</th>
                  <th>Comentarios</th>
                  <th>Registrado Por</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filteredRegistries.map((registry) => (
                  <tr key={registry.id} data-testid={`registry-row-${registry.id}`}>
                    <td className="visitor-name-cell">{registry.room_number}</td>
                    <td>{registry.number_of_people}</td>
                    <td>
                      <div className="destination-tags-inline">
                        {registry.destination.map(dest => (
                          <span key={dest} className="destination-badge-small">{dest}</span>
                        ))}
                      </div>
                    </td>
                    <td>{registry.vehicle_plate || '-'}</td>
                    <td>{registry.vehicle_brand || '-'}</td>
                    <td>{registry.vehicle_color || '-'}</td>
                    <td>{registry.comments || '-'}</td>
                    <td>{registry.registered_by}</td>
                    <td>{formatDate(registry.registered_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityRegistryHistory;
